import { env } from '@/env';
import type { Dict } from 'mixpanel-browser';

export const SENSITIVE_WORD_SEGMENT_TOKENS = [
  'email',
  'emails',
  'emailaddress',
  'phone',
  'phones',
  'phonenumber',
  'mbi',
  'dob',
  'ssn',
  'address',
  'addresses',
  'streetaddress',
  'street',
  'apt',
  'apartment',
  'city',
  'cities',
  'zip',
  'zips',
  'zipcode',
  'postalcode',
  'firstname',
  'lastname',
  'surname',
  'surnames',
  'bloodpressure',
  'gender',
  'genders',
  'diagnosis',
  'diagnoses',
  'condition',
  'conditions',
  'systolic',
  'diastolic',
  'password',
  'passwords',
  'birthday',
  'birthdays',
] as const;

export const SENSITIVE_EXACT_KEY_TOKENS = [
  'name',
  'names',
  'first',
  'last',
  'state',
  'family',
  'given',
  'dateofbirth',
  'birthdate',
  'medicareid',
  'medicarenumber',
  'fullname',
  'patientname',
  'formvalue',
  'formvalues',
  'gad7answers',
  'gad7score',
  'phq9answers',
  'phq9score',
] as const;

const SENSITIVE_WORD_PAIR_TOKENS: ReadonlyArray<readonly [string, string]> = [
  ['first', 'name'],
  ['last', 'name'],
  ['given', 'name'],
  ['family', 'name'],
  ['middle', 'name'],
  ['maiden', 'name'],
  ['blood', 'pressure'],
  ['medicare', 'id'],
  ['postal', 'code'],
];

const SENSITIVE_WORD_SEGMENT_TOKEN_SET = new Set<string>(SENSITIVE_WORD_SEGMENT_TOKENS);
const SENSITIVE_EXACT_KEY_TOKEN_SET = new Set<string>(SENSITIVE_EXACT_KEY_TOKENS);

export const splitIntoWords = (key: string): string[] =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .split(/[^a-zA-Z0-9]+/)
    .map((word) => word.toLowerCase())
    .filter(Boolean);

export const normalizeKey = (key: string): string => {
  const words = splitIntoWords(key);
  if (words.length > 1 && /^\d+$/.test(words[words.length - 1])) {
    words.pop();
  }
  return words.join('');
};

const hasSensitiveWordPair = (words: string[]): boolean =>
  SENSITIVE_WORD_PAIR_TOKENS.some(([first, second]) =>
    words.some(
      (word, index) =>
        word === first && (words[index + 1] === second || words[index + 1] === `${second}s`),
    ),
  );

const isSensitiveKey = (key: string): boolean => {
  if (SENSITIVE_EXACT_KEY_TOKEN_SET.has(normalizeKey(key))) return true;
  const words = splitIntoWords(key);
  if (words.some((word) => SENSITIVE_WORD_SEGMENT_TOKEN_SET.has(word))) return true;
  return hasSensitiveWordPair(words);
};

const isPlainObject = (value: unknown): value is Dict => {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const scrubValue = (value: unknown, ancestors: WeakSet<object>): unknown => {
  if (Array.isArray(value)) {
    if (ancestors.has(value)) return [];
    ancestors.add(value);
    const result = value.map((item) => scrubValue(item, ancestors));
    ancestors.delete(value);
    return result;
  }
  if (isPlainObject(value)) return scrubProperties(value, ancestors);
  return value;
};

export function scrubProperties(properties: Dict, ancestors: WeakSet<object> = new WeakSet()): Dict {
  if (ancestors.has(properties)) return {};
  ancestors.add(properties);

  const scrubbed: Dict = {};
  const dropped: string[] = [];

  Object.entries(properties).forEach(([key, value]) => {
    if (isSensitiveKey(key)) {
      dropped.push(key);
      return;
    }
    scrubbed[key] = scrubValue(value, ancestors);
  });

  ancestors.delete(properties);

  if (dropped.length && env.IS_DEV) {
    console.warn('[Analytics] Dropped sensitive propert(ies) before send:', dropped);
  }

  return scrubbed;
}
